import React from 'react';
import { map, sumBy, filter, forEach, difference, transform } from 'lodash';
import { ANOMALY_DETECTION_KEY, ACS1000_COOLING_ANOMALY_KEY, chartHeightConstants, DRIVE_ANOMALY_DETECTION_KEY, DRIVE_STATUS_KEY, KPI_TYPE, ENERGY_ANOMALY_DETECTION_KEY, OVER_ALL_STATUS } from './constants';
import colors from 'theme/_colors.scss';
import {
    IconAnnualMaintenanceBarChartLegend,
    IconAnnualMaintenancePMThreshold,
    IconCondition,
    IconConditionForecast,
    IconTrendPoorIndicator,
    IconTrendTolerableIndicator,
    IconAnomalySignal
} from 'svgIcons/MotionPortalIcons';
import { assetType, StateID } from 'helpers/constants';
import IconArrow from 'svgIcons/MotionPortalIcons/IconArrow';
import { CHART_CONDITION_TREND_LONG_TERM_KPI_TYPE, CHART_CONDITION_TREND_SHORT_TERM_KPI_TYPE } from './CommonCharts/Trend/constants';
import { CHART_CONDITION_TREND_KPI_TYPE, chartColors } from './CommonCharts/DriveAnomalyDetection/constants';
// import useNewCapability from 'sharedHooks/useNewCapability';
import { translate } from 'helpers/translateHelper';
import { toLocalDateTimeOffsetString } from 'helpers/dateHelper';
// eslint-disable-next-line import/no-unused-modules
export const afterChartReDrawStringData = (chartContainerId, removeFn) => {
    const htmlElements = document.querySelectorAll(`#${chartContainerId} .remove-button`);
    map(htmlElements, element => {
        // Clone the node, in order to remove all previous event listeners
        const newElement = element.cloneNode(true);
        element.parentNode.replaceChild(newElement, element);
        newElement.addEventListener('click', (e) => {
            removeFn(parseInt(e.currentTarget.id.split('_')[1]));
            const highChartLegends = document.querySelectorAll('div.highcharts-legend-item');
            const popoutChartElements = Array.from(highChartLegends).filter((ele) => ele.closest('.popout-chart-container'));
            // Get respective signal elements from the DOM
            const allButtons = popoutChartElements?.length ? popoutChartElements : highChartLegends;
            const hiddenButtons = Array.from(allButtons).filter(ele => ele.classList.contains('highcharts-legend-item-hidden'));
            if (allButtons && hiddenButtons) {
                const nonHiddenCount = allButtons.length - hiddenButtons.length;
                if (nonHiddenCount === 1 && hiddenButtons.length > 0) {
                    const nonHiddenEle = Array.from(allButtons).find(ele => !ele.classList.contains('highcharts-legend-item-hidden'));
                    if (nonHiddenEle.querySelector(`#${e.currentTarget.id}`)) {
                        hiddenButtons[0].querySelector('.item-kpi-name').click();
                    }
                }
            }
            e.stopPropagation();
        });
    });
};
// eslint-disable-next-line import/no-unused-modules
export const chartLegendItemClick = (e) => {
    if (e.target.visible) {
        const totalVisible = sumBy(
            e.target.chart.series,
            ({ visible, xData, index, legendItem }) => {
                return Number(visible === true
                    && (xData.length === 0 && e.target.index === index || xData.length > 0)
                    && !!legendItem);
            }
        );
        if (totalVisible === 1) {
            e.preventDefault();
            return false;
        }
    }
};

export const getDriveStatusData = (driveChartData) => {
    return driveChartData?.conditionTrends?.length ? driveChartData?.conditionTrends : [];

};

export const getDriveOverAllStatus = (driveChartData) => {
    return driveChartData?.currentCondition ? OVER_ALL_STATUS[driveChartData?.currentCondition] : 0;
};

export const getAllKPITrendChartList = (KPIList, data) => {
    if (!KPIList) {
        KPIList = data;
    }
    return KPIList?.filter((trendKPIItem) => ![DRIVE_STATUS_KEY, ANOMALY_DETECTION_KEY].includes(trendKPIItem?.key) && !trendKPIItem?.isAnomaly);
};

export const getDriveKPIList = (KPIList, data) => {
    if (!KPIList) {
        KPIList = data;
    }
    return KPIList?.filter((trendKPIItem) => trendKPIItem?.key === DRIVE_STATUS_KEY);
};

export const getAnomalyDetectionKPI = (KPIList, data) => {
    if (!KPIList) {
        KPIList = data;
    }
    const anomalyDetectionKPI = KPIList?.filter((trendKPIItem) => trendKPIItem?.key === ANOMALY_DETECTION_KEY || trendKPIItem?.key === DRIVE_ANOMALY_DETECTION_KEY || trendKPIItem?.key === ACS1000_COOLING_ANOMALY_KEY || trendKPIItem?.key === ENERGY_ANOMALY_DETECTION_KEY || trendKPIItem?.isAnomaly);
    return anomalyDetectionKPI;
};

export const getCoolingAnomalyKPI = (anomalyDetectionKPIList) => {
    return anomalyDetectionKPIList?.find((kpi) => kpi?.key === DRIVE_ANOMALY_DETECTION_KEY || kpi?.key === ACS1000_COOLING_ANOMALY_KEY);
};

export const getEnergyAnomalyKPI = (anomalyDetectionKPIList) => {
    return anomalyDetectionKPIList?.find((kpi) => kpi?.key === ENERGY_ANOMALY_DETECTION_KEY);
};
export const calculateDynamicChartHeight = (conditionTrendsData, isDriveAnomaly, containerHeight) => {
    const standardHeightCalculation = () => {
        const availableHeight = Math.max(chartHeightConstants.min, containerHeight - chartHeightConstants.reserved);
        return Math.min(availableHeight, chartHeightConstants.max);
    };

    // For motor anomaly, use standard height calculation
    if (!isDriveAnomaly) {
        return standardHeightCalculation();
    }

    // For drive anomaly, count signals (excluding 'Kpi' type)
    const signalCount = conditionTrendsData?.filter(trend => trend.type !== CHART_CONDITION_TREND_KPI_TYPE)?.length || 0;
    // Only increase height to 600px when there are MORE than 7 signals
    if (signalCount > 7) {
        const increasedHeight = 600;

        const minHeight = Math.max(chartHeightConstants.min, increasedHeight);
        const maxHeight = Math.min(minHeight, chartHeightConstants.max);
        return maxHeight;
    }
    return standardHeightCalculation();
};

export const getDateRange = () => {
    const fromDate = new Date();
    fromDate.setDate(fromDate?.getDate() - 89);
    const toDate = new Date();
    return {
        from: toLocalDateTimeOffsetString(fromDate),
        to: toLocalDateTimeOffsetString(toDate),
    };
};

export const getConditionStatus = (status) => {
    return OVER_ALL_STATUS[status];
};

// Helper function to determine the worst condition
export const getWorstCondition = (conditions) => {
    return conditions?.reduce((worst, current) => {
        const worstPriority = OVER_ALL_STATUS[worst] || 0;
        const currentPriority = OVER_ALL_STATUS[current] || 0;
        return currentPriority > worstPriority ? current : worst;
    });
};

//Combine Threhold, Long term and short term chart to a single chart
export const combinedTrendCharts = (trendKPI) => {
    const combinedTrendData = [];
    map(trendKPI, kpi => {
        let conditionEvents = kpi.conditionEvents;
        if (kpi.key.includes(KPI_TYPE.Threshold) || kpi.key.includes(KPI_TYPE.DcVoltage)) {
            const getLongAndShortTerm = filter(trendKPI, getKPI => getKPI.name.includes(kpi.name) && (
                getKPI.key.includes(KPI_TYPE.ShortTerm) || getKPI.key.includes(KPI_TYPE.LongTerm)
            ));
            let conditionTrends = [];
            let overallCurrentCondition = kpi.currentCondition; // Threshold condition

            if (getLongAndShortTerm?.length) {
                // Assign short and long term condition events data to threshold chart
                conditionEvents = map(getLongAndShortTerm, filteredKPI => filteredKPI.conditionEvents)?.flat();
                conditionTrends = map(getLongAndShortTerm, filteredKPI => {
                    if (filteredKPI.key.includes(KPI_TYPE.ShortTerm)) {
                        return {
                            type: CHART_CONDITION_TREND_SHORT_TERM_KPI_TYPE,
                            values: filteredKPI.conditionTrends?.[0]?.values,
                        };
                    }
                    return {
                        type: CHART_CONDITION_TREND_LONG_TERM_KPI_TYPE,
                        values: filteredKPI.conditionTrends?.[0]?.values,
                    };
                });
                // Get all current conditions from long and short term
                const allConditions = [
                    kpi.currentCondition, // threshold condition
                    ...map(getLongAndShortTerm, filteredKPI => filteredKPI.currentCondition)
                ];
                overallCurrentCondition = getWorstCondition(allConditions);
            }

            combinedTrendData.push({
                ...kpi,
                conditionEvents,
                currentCondition: overallCurrentCondition, // Use the worst condition
                conditionTrends: [
                    ...kpi?.conditionTrends ?? [],
                    ...conditionTrends
                ]
            });
        }
    });
    return combinedTrendData;
};

export const chartLegendItems = (assetDetails) => [
    {
        title: 'ABB.Powertrain.Frontend.graphLegendTitle',
        legends: [
            {
                icon: <IconAnnualMaintenancePMThreshold color={colors.black} width={36} height={16} />,
                translateKey: 'ABB.Powertrain.Frontend.legendSignalLabel'
            },
            {
                icon: <IconAnnualMaintenancePMThreshold color={colors.pomegranate} width={36} height={16} />,
                translateKey: 'ABB.Powertrain.Frontend.conditionTrendTooltipPoor'
            },
            {
                icon: <IconAnnualMaintenancePMThreshold color={colors.orangePeel} width={36} height={16} />,
                translateKey: 'ABB.Powertrain.Frontend.conditionTrendTooltipTolerable'
            },
            {
                icon: <div className='legend-event-icons'>
                    <IconCondition condition={StateID.POOR} />
                    <IconCondition condition={StateID.TOLERABLE} />
                </div>,
                translateKey: 'ABB.Powertrain.Frontend.legendThresholdMarker',
            },
            ...assetDetails?.assetType?.assetType !== assetType.DRIVE ? [{
                icon: <div className='legend-event-icons'>
                    <IconTrendPoorIndicator />
                    <IconTrendTolerableIndicator />
                </div>,
                translateKey: 'ABB.Powertrain.Frontend.legendTrendMarker',
            },
            {
                icon: <div className='legend-event-icons arrows'>
                    <IconArrow color={colors.pomegranate} />
                    <IconArrow color={colors.orangePeel} />
                </div>,
                translateKey: 'ABB.Powertrain.Frontend.legendTrendIndicator',
            }] : [],
            {
                icon: <IconConditionForecast width={36} height={16} />,
                translateKey: 'ABB.Powertrain.Frontend.conditionForecast'
            },
        ]
    },
    {
        title: 'ABB.Powertrain.Frontend.statusBarLegendTitle',
        legends: [
            {
                icon: <IconAnnualMaintenanceBarChartLegend
                    width={36}
                    viewBox={'0 0 36 16'}
                    borderRadius={0}
                    color={colors.pomegranate}
                />,
                translateKey: 'ABB.Powertrain.Frontend.withAlarm',
            },
            {
                icon: <IconAnnualMaintenanceBarChartLegend
                    width={36}
                    viewBox={'0 0 36 16'}
                    borderRadius={0}
                    color={colors.orangePeel}
                />,
                translateKey: 'ABB.Powertrain.Frontend.withAlert',
            },
            {
                icon: <IconAnnualMaintenanceBarChartLegend
                    width={36}
                    viewBox={'0 0 36 16'}
                    borderRadius={0}
                    color={colors.silver}
                />,
                translateKey: 'ABB.Powertrain.Frontend.unknownLabel',
            },
            {
                icon: <IconAnnualMaintenanceBarChartLegend
                    width={36}
                    viewBox={'0 0 36 16'}
                    borderRadius={0}
                    color={colors.jungleGreen}
                />,
                translateKey: 'ABB.Powertrain.Frontend.goodLabel',
            },
        ]
    }
];

export const conditionInsightsTabs = {
    CONDITION_INDICES: 'condition indices',
    ANOMALY_DETECTION: 'Anomaly detection'
};

// const getTabsVisibility = () => {
//     const capability = useNewCapability();
//     return {
//         [conditionInsightsTabs.CONDITION_INDICES]: true,
//         [conditionInsightsTabs.ANOMALY_DETECTION]: true,
//     };
// };

export const getToggleOptions = () => {
    const allTabs = [
        {
            label: translate('ABB.Powertrain.Frontend.conditionIndicesTab'),
            value: conditionInsightsTabs.CONDITION_INDICES,
        },
        {
            label: translate('ABB.Powertrain.Frontend.anomalyDetectionTab'),
            value: conditionInsightsTabs.ANOMALY_DETECTION,
        }
    ];
    // const tabVisibility = getTabsVisibility();

    // return filter(allTabs, (tab) => tabVisibility[tab.value]);
    return allTabs;
};

export const anomalyChartLegendItems = (isCoolingAnomalyActive) => {
    const legendItems = [
        {
            title: 'ABB.Powertrain.Frontend.anomalySatusBarLegendTitle',
            legends: [
                {
                    icon: <IconAnnualMaintenanceBarChartLegend
                        width={36}
                        viewBox={'0 0 36 16'}
                        borderRadius={0}
                        color={colors.orangePeel}
                    />,
                    translateKey: 'ABB.Powertrain.Frontend.withAnomaly',
                },
                {
                    icon: <IconAnnualMaintenanceBarChartLegend
                        width={36}
                        viewBox={'0 0 36 16'}
                        borderRadius={0}
                        color={colors.silver}
                    />,
                    translateKey: 'ABB.Powertrain.Frontend.unknownLabel',
                },
                {
                    icon: <IconAnnualMaintenanceBarChartLegend
                        width={36}
                        viewBox={'0 0 36 16'}
                        borderRadius={0}
                        color={colors.jungleGreen}
                    />,
                    translateKey: 'ABB.Powertrain.Frontend.goodLabel',
                },
            ]
        }
    ];

    // Add anomaly points legend for all anomaly charts except cooling anomaly
    if (!isCoolingAnomalyActive) {
        legendItems.unshift({
            title: 'ABB.Powertrain.Frontend.graphLegendTitle',
            legends: [
                {
                    icon: <IconAnomalySignal width={36} height={10} viewBox={'0 0 36 10'} />,
                    translateKey: 'ABB.Powertrain.Frontend.anomalyPoints'
                },
            ]
        });
    }

    return legendItems;
};

export const setColor = (list) => {
    const usedColors = transform(list, (acc, x) => {
        if (x.color) {
            acc.push(x.color);
        }
    }, []);

    const availableColors = difference(chartColors, usedColors);
    forEach(list, (item) => {
        if (!item.color) {
            item.color = availableColors.length ? availableColors.splice(0, 1)[0] : null;
        }
    });
    return list;
};
